import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';
import AnimationLayer from '../components/desktop/AnimationLayer';
import { getThemeAnimations } from '@theme/animation-config';
import { ThemeCustomizerButton, SiteThemeCustomizer } from '../components/desktop/ThemeCustomizer/index';
import { useScale, scaleW, scaleH } from '../hooks/useScale';
import { Bell, Heart, MessageSquare, Edit3, Users, LogOut, ChevronDown } from 'lucide-react';
import LoadingScreen from '../components/desktop/LoadingScreen';
import { useAuth } from '../context/AuthContext';
import MainNavbar from '../components/desktop/MainNavbar';
import { getLegacyPostUrl, getCommunityPostUrl, getForumPostUrl } from '../utils/postUrls';
import { notificationEventBus } from '../lib/notificationEventBus';
import { useNotificationSync } from '../hooks/useNotificationSync';
import { getBackground, getCardBackground, calculateNavbarBg } from '../utils/backgroundHelpers';
import resolveAvatar from '../lib/resolveAvatar';
import { cacheCurrentPageTheme, getCachedTheme } from '../utils/themeCache';

import { fetchWithAuth } from '../lib/fetchWithAuth';
type Friend = {
  id: string;
  username: string;
  displayName: string;
  avatar?: string;
  profileTitle?: string;
  profileFrameShape?: 'square' | 'circle';
  profileFrameDesign?: 'coral' | 'waves' | 'bubbles';
  mutualFriends?: number;
};

export default function Friends() {
  const navigate = useNavigate();
  const { theme, currentPreset, siteCustomization, siteAnimations, ensureSiteThemeApplied } = useTheme();
  const { user: currentUser, loading: authLoading, logout, setShowLoginModal } = useAuth();
  const { setCurrentTheme } = useNavigation();
  const scale = useScale();
  const [loading, setLoading] = useState(true);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [themeAnimations, setThemeAnimations] = useState(() => {
    // UNIFIED CACHE: Use animations from ThemeContext
    if (siteAnimations) {
      return siteAnimations;
    }
    // Fallback to preset animations
    return getThemeAnimations(currentPreset);
  });
  const [showSiteCustomizer, setShowSiteCustomizer] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // Use theme from ThemeContext directly (already has computedColors applied)
  const localTheme = React.useMemo(() => {
        return {
      ...theme,
      textColor: theme.textColor,
      accentColor: theme.accentColor,
      primaryColor: theme.primaryColor,
      secondaryColor: theme.secondaryColor,
      cardBg: theme.cardBg,
      cardGradientColor1: theme.cardGradientColor1,
      cardGradientColor2: theme.cardGradientColor2,
      cardGradientEnabled: theme.cardGradientEnabled,
      navbarBg: theme.navbarBg,
      tabBarBg: theme.tabBarBg,
    };
  }, [theme]);

  const themeColors = {
    navbarBg: localTheme.navbarBg || calculateNavbarBg(localTheme.primaryColor!, 92),
    accentColor: localTheme.accentColor!,
    textColor: localTheme.textColor!,
    tabBarBg: localTheme.tabBarBg || calculateNavbarBg(localTheme.primaryColor!, 92),
    gradientFrom: localTheme.primaryColor!,
    gradientTo: localTheme.secondaryColor!
  };

  // Register site theme with NavigationContext - ONLY AFTER loading is complete
  // CRITICAL: Do NOT call setCurrentTheme during loading, or it will overwrite the previous page's theme
  // and the loading screen will show site colors instead of profile/community colors
  useEffect(() => {
    if (loading) return;
    if (!siteCustomization) {
      // Site theme was fetched while a profile/community page was active — apply it now.
      ensureSiteThemeApplied();
      return;
    }
    if (theme?.primaryColor) {
      console.log('[Friends] 🎨 Setting currentTheme for loading screen (after loading complete):');
      setCurrentTheme({
        type: 'site',
        colors: {
          primaryColor: theme.primaryColor,
          secondaryColor: theme.secondaryColor || theme.primaryColor,
          textColor: theme.textColor || '#ffffff',
          accentColor: theme.accentColor || theme.primaryColor
        }
      });
    }
  }, [loading, theme?.primaryColor, theme?.secondaryColor, theme?.textColor, theme?.accentColor, siteCustomization, setCurrentTheme, ensureSiteThemeApplied]);

  // Helper to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha / 100})`;
  };

  // Apply customization as CSS variables when localTheme changes
  useEffect(() => {
    
    const root = document.documentElement;

    // Helper to blend two colors
    const blendColors = (color1: string, color2: string, ratio: number) => {
      
      // Validate inputs
      if (!color1 || typeof color1 !== 'string' || !color1.startsWith('#') || color1.length < 7) {
        console.error('❌ [Friends] Invalid color1 in blendColors:', color1);
        return color2 || '#000000';
      }
      if (!color2 || typeof color2 !== 'string' || !color2.startsWith('#') || color2.length < 7) {
        console.error('❌ [Friends] Invalid color2 in blendColors:', color2);
        return color1;
      }

      const r1 = parseInt(color1.slice(1, 3), 16);
      const g1 = parseInt(color1.slice(3, 5), 16);
      const b1 = parseInt(color1.slice(5, 7), 16);

      const r2 = parseInt(color2.slice(1, 3), 16);
      const g2 = parseInt(color2.slice(3, 5), 16);
      const b2 = parseInt(color2.slice(5, 7), 16);

      // Check for NaN
      if (isNaN(r1) || isNaN(g1) || isNaN(b1)) {
        console.error('❌ [Friends] Failed to parse color1 (NaN):', { color1, r1, g1, b1 });
        return color2;
      }
      if (isNaN(r2) || isNaN(g2) || isNaN(b2)) {
        console.error('❌ [Friends] Failed to parse color2 (NaN):', { color2, r2, g2, b2 });
        return color1;
      }

      const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
      const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
      const b = Math.round(b1 * (1 - ratio) + b2 * ratio);

      const result = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
            return result;
    };

    // Apply background gradient
    if ((localTheme as any).gradientEnabled) {
      const ratio = ((localTheme as any).gradientFavorability || 50) / 100;
      const blendedColor1 = blendColors(localTheme.primaryColor!, localTheme.secondaryColor!, ratio);
      const blendedColor2 = blendColors(localTheme.secondaryColor!, localTheme.primaryColor!, ratio);
      const strength = ((localTheme as any).gradientStrength || 100) / 100;
      const finalColor1 = blendColors('#808080', blendedColor1, strength);
      const finalColor2 = blendColors('#808080', blendedColor2, strength);

      const gradient = `linear-gradient(${(localTheme as any).gradientAngle || 135}deg, ${finalColor1}, ${finalColor2})`;
            root.style.setProperty('--app-bg', gradient);
    } else {
            root.style.setProperty('--app-bg', localTheme.primaryColor!);
    }

    // Apply other CSS variables (using transformed colors from localTheme)
    root.style.setProperty('--color-text', localTheme.textColor!);
    root.style.setProperty('--color-accent', localTheme.accentColor!);
    root.style.setProperty('--navbar-bg', localTheme.navbarBg!);
    root.style.setProperty('--tab-bar-bg', localTheme.tabBarBg!);

    // Apply card background with gradient if enabled
    if (localTheme.cardGradientEnabled) {
      const ratio = ((localTheme as any).cardGradientFavorability || 50) / 100;
      const blendedCard1 = blendColors(localTheme.cardGradientColor1!, localTheme.cardGradientColor2!, ratio);
      const blendedCard2 = blendColors(localTheme.cardGradientColor2!, localTheme.cardGradientColor1!, ratio);
      const strength = ((localTheme as any).cardGradientStrength || 100) / 100;
      // Use cardGradientColor1 as base if cardBg is a gradient
      const baseCardColor = localTheme.cardBg?.startsWith('#') ? localTheme.cardBg : localTheme.cardGradientColor1!;
      const finalCard1 = blendColors(baseCardColor, blendedCard1, strength);
      const finalCard2 = blendColors(baseCardColor, blendedCard2, strength);

      const gradientBg = `linear-gradient(${(localTheme as any).cardGradientAngle || 120}deg, ${hexToRgba(finalCard1, (localTheme as any).cardOpacity || 100)}, ${hexToRgba(finalCard2, (localTheme as any).cardOpacity || 100)})`;
      root.style.setProperty('--card-bg', gradientBg);
    } else {
      root.style.setProperty('--card-bg', hexToRgba(localTheme.cardBg, (localTheme as any).cardOpacity || 100));
    }

    // Cache the transformed theme for instant loading on next page load
    const appBg = (localTheme as any).gradientEnabled
      ? root.style.getPropertyValue('--app-bg')
      : localTheme.primaryColor!;

    const cardBg = root.style.getPropertyValue('--card-bg');

    cacheCurrentPageTheme({
      textColor: localTheme.textColor!,
      accentColor: localTheme.accentColor!,
      cardBg: cardBg || localTheme.cardBg,
      appBg: appBg || localTheme.primaryColor!,
      navbarBg: localTheme.navbarBg!,
      tabBarBg: localTheme.tabBarBg!,
      primaryColor: localTheme.primaryColor!,
      secondaryColor: localTheme.secondaryColor!,
      cardGradientColor1: localTheme.cardGradientColor1!,
      cardGradientColor2: localTheme.cardGradientColor2!,
      colorSpace: (localTheme as any).colorSpace || 'Off',
      themeType: 'site', // REQUIRED: themeType property for CachedThemeData
      pageType: 'friends',
      userId: currentUser?.id?.toString(),
      animations: themeAnimations,
    });
  }, [localTheme, currentUser?.id, themeAnimations]);

  // Sync animations when siteAnimations or currentPreset changes
  useEffect(() => {
    if (siteAnimations) {
      setThemeAnimations(siteAnimations);
    } else {
      setThemeAnimations(getThemeAnimations(currentPreset));
    }
  }, [siteAnimations, currentPreset]);

  // Theme updates are handled automatically by ThemeContext
  // Listen for theme updates from customizer for animations only
  useEffect(() => {
    const handleThemeUpdate = (event: any) => {
      const { animations, animationsEnabled: enabled } = event.detail;

      // Fix and validate animations object structure before updating
      if (animations) {
        const fixedAnimations = { ...animations };

        // Fix incomplete bubbles structure
        if (fixedAnimations.bubbles && typeof fixedAnimations.bubbles === 'object') {
          if (!fixedAnimations.bubbles.id || !fixedAnimations.bubbles.type || !fixedAnimations.bubbles.count) {
            // If bubbles is incomplete, create proper structure based on enabled state
            fixedAnimations.bubbles = fixedAnimations.bubbles.enabled
              ? {
                  id: 'bubbles',
                  type: 'rising',
                  enabled: true,
                  count: 25,
                  speed: 0.8,
                  size: { min: 15, max: 35 },
                }
              : null;
          }
        }

        // Basic structure validation - just ensure arrays exist and characters has at least one entry with an ID
        const hasValidStructure =
          fixedAnimations.characters && Array.isArray(fixedAnimations.characters) &&
          fixedAnimations.decorations && Array.isArray(fixedAnimations.decorations) &&
          fixedAnimations.characters.length > 0 &&
          fixedAnimations.characters[0]?.id;

        if (hasValidStructure) {
          setThemeAnimations(fixedAnimations);
        }
      }
    };

    window.addEventListener('siteThemeUpdated', handleThemeUpdate);
    return () => window.removeEventListener('siteThemeUpdated', handleThemeUpdate);
  }, []);

  // Notifications
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const navItems = [
    { label: 'Home', path: '/feed' },
    { label: 'Friends', path: '/friends' },
    { label: 'Messages', path: '/messages' },
    { label: 'Community', path: '/community' },
    { label: 'Ads', path: '/ads' },
    { label: 'Marketplace', path: '/marketplace' }
  ];

  const PixelatedTurtle = ({ size = 32 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" className="pixelated">
      <g fill="#10b981">
        <rect x="12" y="8" width="8" height="8" />
        <rect x="10" y="10" width="2" height="4" />
        <rect x="20" y="10" width="2" height="4" />
        <rect x="8" y="14" width="4" height="4" />
        <rect x="20" y="14" width="4" height="4" />
        <rect x="6" y="12" width="2" height="2" />
        <rect x="24" y="12" width="2" height="2" />
        <rect x="10" y="18" width="2" height="2" />
        <rect x="20" y="18" width="2" height="2" />
        <rect x="14" y="6" width="4" height="2" />
      </g>
    </svg>
  );

  useEffect(() => {
    async function loadData() {
      // Track when data loading started
      const loadStartTime = Date.now();

      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      // If not logged in after auth loads, don't fetch data
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        // Fetch friends from API
        const { getFriends } = await import('../lib/api-client');
        const friendsData = await getFriends();
        setFriends(friendsData);
      } catch (friendsError) {
        // If getFriends fails, just set empty array (user might have no friends)
        setFriends([]);
      } finally {
        // Ensure loading screen stays visible for at least 200ms to show previous page's theme
        const loadTime = Date.now() - loadStartTime;
        const minLoadTime = 200;
        if (loadTime < minLoadTime) {
          setTimeout(() => {
            setLoading(false);
          }, minLoadTime - loadTime);
        } else {
          setLoading(false);
        }
      }
    }

    loadData();
  }, [currentUser, authLoading]);

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetchWithAuth('/api/notifications', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      } catch (error) {
      }
    };

    fetchNotifications();
  }, []);

  // Notification synchronization for real-time updates
  useNotificationSync({
    onNew: (notification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    },
    onRead: (notificationId) => {
      setNotifications(prev => prev.map(n => n.id === notificationId ? {...n, read: true} : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    },
    onReadAll: () => {
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    },
    onDelete: (notificationId) => {
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    },
  });

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'FOLLOW':
        return <Users size={20} style={{ color: localTheme.textColor }} />;
      case 'NEW_FRIEND':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
        return <MessageSquare size={20} style={{ color: localTheme.textColor }} />;
      case 'PROFILE_POST':
        return <Edit3 size={20} style={{ color: localTheme.textColor }} />;
      case 'LIKE':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      default:
        return <Bell size={20} style={{ color: localTheme.textColor }} />;
    }
  };

  const getNotificationMessage = (notification: any) => {
    const actor = notification.actorName || 'Someone';
    switch (notification.type) {
      case 'FOLLOW':
        return `${actor} started following you`;
      case 'NEW_FRIEND':
        return `You and ${actor} are now friends!`;
      case 'REPLY':
        return `${actor} replied to your post`;
      case 'COMMUNITY_REPLY':
        return `${actor} replied to your community post`;
      case 'PROFILE_POST':
        return `${actor} posted on your profile`;
      case 'LIKE':
        return `${actor} liked your post`;
      default:
        return notification.content || 'New notification';
    }
  };

  const formatNotificationTime = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: any) => {
    setShowNotifications(false);

    switch (notification.type) {
      case 'FOLLOW':
      case 'NEW_FRIEND':
        if (notification.actorUsername) {
          navigate(`/u/${notification.actorUsername}`);
        } else if (notification.actorId) {
          navigate(`/user/${notification.actorId}`);
        }
        break;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
      case 'LIKE':
      case 'DISLIKE':
      case 'REPOST':
        if (notification.postType === 'forum' && notification.communitySlug && notification.postSlug) {
          navigate(getForumPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'community' && notification.communitySlug && notification.postSlug) {
          navigate(getCommunityPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'legacy' && notification.postAuthorUsername && notification.postSlug) {
          navigate(getLegacyPostUrl(notification.postAuthorUsername, notification.postSlug));
        } else {
          navigate('/feed');
        }
        break;
      case 'PROFILE_POST':
        navigate('/user');
        break;
      case 'MESSAGE':
        navigate('/messages');
        break;
    }
  };

  const handleLogout = async () => {
    logout();
  };

  const handleToggleSiteCustomizer = useCallback(() => {
    setShowSiteCustomizer(prev => !prev);
  }, []);

  const handleCloseSiteCustomizer = useCallback(() => {
    setShowSiteCustomizer(false);
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen w-full relative overflow-x-hidden" style={{ background: getBackground(localTheme as any, {}, theme.appBg), color: localTheme.textColor }}>
      <style>{`
        @keyframes wavyLine {
          0% {
            d: path('M 0,50 Q 25,45 50,50 T 100,50');
          }
          50% {
            d: path('M 0,50 Q 25,55 50,50 T 100,50');
          }
          100% {
            d: path('M 0,50 Q 25,45 50,50 T 100,50');
          }
        }
        .pixelated {
          image-rendering: pixelated;
          image-rendering: -moz-crisp-edges;
          image-rendering: crisp-edges;
        }
      `}</style>

      <AnimationLayer
        animations={themeAnimations}
        isMobile={false}
        bubbleColor={localTheme.accentColor}
      />

      {/* Top Navigation Bar */}
      <MainNavbar
        currentPath="/friends"
        onLogout={handleLogout}
        onSignIn={() => setShowLoginModal(true)}
        currentUser={currentUser}
        notifications={notifications}
        unreadCount={unreadCount}
        theme={localTheme}
        customization={localTheme as any}
        navbarBg={localTheme.navbarBg}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        onNotificationClick={handleNotificationClick}
        getNotificationIcon={getNotificationIcon}
        getNotificationMessage={getNotificationMessage}
        formatNotificationTime={formatNotificationTime}
        setNotifications={setNotifications}
        setUnreadCount={setUnreadCount}
      />

      {/* Page Title */}
      <div className="relative z-10 w-full" style={{ paddingLeft: '4rem', paddingTop: '3rem', paddingBottom: '1.5rem' }}>
        <h1 className="text-5xl font-bold" style={{ color: localTheme.textColor }}>
          Friends
        </h1>
      </div>

      {/* Wavy Line - Full Width */}
      <div className="relative w-full mb-8" style={{ height: '100px', overflow: 'hidden' }}>
        <svg width="100%" height="100" xmlns="http://www.w3.org/2000/svg" className="absolute top-0 left-0" preserveAspectRatio="none" viewBox="0 0 2000 100">
          <defs>
            <linearGradient id="wavyGradientFriends" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: localTheme.accentColor, stopOpacity: 0.6 }} />
              <stop offset="50%" style={{ stopColor: localTheme.accentColor, stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: localTheme.accentColor, stopOpacity: 0.6 }} />
            </linearGradient>
          </defs>
          <path
            stroke="url(#wavyGradientFriends)"
            strokeWidth="3"
            fill="none"
          >
            <animate
              attributeName="d"
              dur="4s"
              repeatCount="indefinite"
              values="
                M 0,50 Q 100,35 200,50 Q 300,65 400,50 Q 500,35 600,50 Q 700,65 800,50 Q 900,35 1000,50 Q 1100,65 1200,50 Q 1300,35 1400,50 Q 1500,65 1600,50 Q 1700,35 1800,50 Q 1900,65 2000,50;
                M 0,50 Q 100,50 200,50 Q 300,50 400,50 Q 500,50 600,50 Q 700,50 800,50 Q 900,50 1000,50 Q 1100,50 1200,50 Q 1300,50 1400,50 Q 1500,50 1600,50 Q 1700,50 1800,50 Q 1900,50 2000,50;
                M 0,50 Q 100,65 200,50 Q 300,35 400,50 Q 500,65 600,50 Q 700,35 800,50 Q 900,65 1000,50 Q 1100,35 1200,50 Q 1300,65 1400,50 Q 1500,35 1600,50 Q 1700,65 1800,50 Q 1900,35 2000,50;
                M 0,50 Q 100,50 200,50 Q 300,50 400,50 Q 500,50 600,50 Q 700,50 800,50 Q 900,50 1000,50 Q 1100,50 1200,50 Q 1300,50 1400,50 Q 1500,50 1600,50 Q 1700,50 1800,50 Q 1900,50 2000,50;
                M 0,50 Q 100,35 200,50 Q 300,65 400,50 Q 500,35 600,50 Q 700,65 800,50 Q 900,35 1000,50 Q 1100,65 1200,50 Q 1300,35 1400,50 Q 1500,65 1600,50 Q 1700,35 1800,50 Q 1900,65 2000,50
              "
              keyTimes="0; 0.25; 0.5; 0.75; 1"
              calcMode="spline"
              keySplines="0.4 0 0.6 1; 0.4 0 0.6 1; 0.4 0 0.6 1; 0.4 0 0.6 1"
            />
          </path>
        </svg>
      </div>

      {/* Friends List - Horizontal Scrolling Sections with 3x4 Grid */}
      <div className="relative z-10 w-full pb-12">
        {friends.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <PixelatedTurtle size={100} />
            <p className="mt-6 text-xl" style={{ color: localTheme.textColor + '99' }}>
              No mutual friends yet; invite your friends to the platform
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-visible" style={{ padding: `${scaleH(16, scale.h)}px ${scaleW(48, scale.w)}px` }}>
            <div className="flex" style={{ gap: `${scaleW(48, scale.w)}px` }}>
              {/* Split friends into sections of 12 (3 columns × 4 rows) */}
              {Array.from({ length: Math.ceil(friends.length / 12) }, (_, sectionIndex) => (
                <div key={sectionIndex} className="flex-shrink-0">
                  {/* 3x4 Grid within each section */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gridTemplateRows: 'repeat(4, auto)',
                    gap: `${scaleH(32, scale.h)}px ${scaleW(24, scale.w)}px`,
                    gridAutoFlow: 'row',
                    overflow: 'visible',
                    padding: `${scaleH(8, scale.h)}px 0`
                  }}>
                    {friends.slice(sectionIndex * 12, (sectionIndex + 1) * 12).map((friend) => (
              <Link
                key={friend.id}
                to={`/user/${friend.username}`}
                className="rounded-lg backdrop-blur-sm border transition-all hover:scale-105 text-left flex-shrink-0 relative block"
                style={{
                  background: getCardBackground(localTheme as any, {}, theme.cardBg),
                  borderColor: localTheme.accentColor + '40',
                  width: `${scaleW(240, scale.w)}px`,
                  padding: `${scaleH(24, scale.h)}px ${scaleW(20, scale.w)}px ${scaleH(20, scale.h)}px ${scaleW(20, scale.w)}px`,
                  zIndex: 20
                }}
              >
                <div className="flex flex-col items-center">
                  {/* Profile Picture with Frame */}
                  <div className="relative" style={{ marginBottom: `${scaleH(16, scale.h)}px` }}>
                    <div
                      className={`flex items-center justify-center overflow-hidden ${friend.profileFrameShape === 'circle' ? 'rounded-full' : 'rounded-lg'}`}
                      style={{
                        border: `${scaleW(3, scale.w)}px solid ${localTheme.accentColor}`,
                        backgroundColor: localTheme.accentColor + '20',
                        width: `${scaleW(96, scale.w)}px`,
                        height: `${scaleW(96, scale.w)}px`
                      }}
                    >
                      {resolveAvatar(friend.avatar) ? (
                        <img src={resolveAvatar(friend.avatar)} alt={friend.displayName} className="w-full h-full object-cover" />
                      ) : (
                        <PixelatedTurtle size={scaleW(48, scale.w)} />
                      )}
                    </div>

                    {/* Profile Decoration */}
                    {friend.profileFrameDesign && (
                      <div className="absolute flex items-center justify-center" style={{ top: `${scaleH(-4, scale.h)}px`, right: `${scaleW(-4, scale.w)}px`, width: `${scaleW(40, scale.w)}px`, height: `${scaleW(40, scale.w)}px` }}>
                        {friend.profileFrameDesign === 'coral' && (
                          <svg width={scaleW(40, scale.w)} height={scaleW(40, scale.w)} viewBox="0 0 32 32" className="pixelated">
                            <g fill={localTheme.accentColor}>
                              <rect x="14" y="8" width="4" height="4" />
                              <rect x="12" y="12" width="8" height="4" />
                              <rect x="10" y="16" width="4" height="4" />
                              <rect x="18" y="16" width="4" height="4" />
                              <rect x="14" y="20" width="4" height="4" />
                            </g>
                          </svg>
                        )}
                        {friend.profileFrameDesign === 'waves' && (
                          <svg width={scaleW(40, scale.w)} height={scaleW(40, scale.w)} viewBox="0 0 32 32" className="pixelated">
                            <g fill={localTheme.accentColor}>
                              <rect x="8" y="14" width="4" height="4" />
                              <rect x="12" y="12" width="4" height="4" />
                              <rect x="16" y="14" width="4" height="4" />
                              <rect x="20" y="12" width="4" height="4" />
                            </g>
                          </svg>
                        )}
                        {friend.profileFrameDesign === 'bubbles' && (
                          <svg width={scaleW(40, scale.w)} height={scaleW(40, scale.w)} viewBox="0 0 32 32" className="pixelated">
                            <g fill={localTheme.accentColor}>
                              <circle cx="12" cy="12" r="3" />
                              <circle cx="20" cy="10" r="2" />
                              <circle cx="18" cy="18" r="2.5" />
                            </g>
                          </svg>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Name */}
                  <h3 className="font-bold" style={{ color: localTheme.textColor, fontSize: `${scaleW(20, scale.w)}px`, marginBottom: `${scaleH(4, scale.h)}px` }}>
                    {friend.displayName}
                  </h3>

                  {/* Username */}
                  <p style={{ color: localTheme.textColor + '99', fontSize: `${scaleW(14, scale.w)}px`, marginBottom: `${scaleH(8, scale.h)}px` }}>
                    @{friend.username}
                  </p>

                  {/* Title */}
                  <p style={{ color: localTheme.textColor + 'CC', fontSize: `${scaleW(14, scale.w)}px`, marginBottom: `${scaleH(12, scale.h)}px` }}>
                    {friend.profileTitle || 'Earthling'}
                  </p>

                  {/* Mutual Friends */}
                  {friend.mutualFriends !== undefined && (
                    <div className="flex items-center" style={{ gap: `${scaleW(8, scale.w)}px`, marginTop: `${scaleH(8, scale.h)}px` }}>
                      <div className="flex" style={{ marginLeft: `${scaleW(-8, scale.w)}px` }}>
                        <div className="rounded-full flex items-center justify-center" style={{
                          backgroundColor: localTheme.accentColor + '40',
                          border: `${scaleW(2, scale.w)}px solid ${localTheme.cardBg}`,
                          width: `${scaleW(24, scale.w)}px`,
                          height: `${scaleW(24, scale.w)}px`
                        }}>
                          <PixelatedTurtle size={scaleW(12, scale.w)} />
                        </div>
                        <div className="rounded-full flex items-center justify-center" style={{
                          backgroundColor: localTheme.accentColor + '40',
                          border: `${scaleW(2, scale.w)}px solid ${localTheme.cardBg}`,
                          width: `${scaleW(24, scale.w)}px`,
                          height: `${scaleW(24, scale.w)}px`,
                          marginLeft: `${scaleW(-8, scale.w)}px`
                        }}>
                          <PixelatedTurtle size={scaleW(12, scale.w)} />
                        </div>
                      </div>
                      <p style={{ color: localTheme.textColor + '99', fontSize: `${scaleW(12, scale.w)}px` }}>
                        {friend.mutualFriends} mutual {friend.mutualFriends === 1 ? 'friend' : 'friends'}
                      </p>
                    </div>
                  )}
                </div>
              </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Standardized Theme Customizer Button */}
      <ThemeCustomizerButton
        onClick={handleToggleSiteCustomizer}
        tooltipText="Customize Site Theme"
        accentColor={localTheme.accentColor}
        textColor={localTheme.textColor}
        show={true}
      />

      {/* Site Theme Customizer Modal */}
      <SiteThemeCustomizer
        isOpen={showSiteCustomizer}
        onClose={handleCloseSiteCustomizer}
        userId={currentUser?.id}
        initialAnimations={themeAnimations}
      />
    </div>
  );
}
